import { Indicator, Menu, Tooltip, UnstyledButton, useMantineTheme } from '@mantine/core';
import { useOverrideHooks } from '@override';

import { ReportStatus, ReportTypes } from '@e-origin/shared';

import { reportIcon } from '@assets';
import { IconBox } from '@components';
import { useDownloadReportFile, useDownloadUtbReport } from '@hooks/batch';
import { useGetUserReports } from '@hooks/reports';
import { useDeleteReport } from '@hooks/reports/useDeleteReport';
import { ReportMenuItem } from './ReportMenuItem';

// eslint-disable-next-line max-statements
export const ReportMenu = () => {
  const { data: reports } = useGetUserReports();
  const theme = useMantineTheme();
  const OverrideHooks = useOverrideHooks();

  const downloadReportFileMutation = useDownloadReportFile();
  const downloadBatchReportMutation = OverrideHooks.useDownloadBatchReport();
  const downloadUtbReportMutation = useDownloadUtbReport();
  const deleteReportMutation = useDeleteReport();

  const handleDownload = (report: (typeof reports)[number]) => {
    const batchId = (report.payload[report.reportType] as { batchId: string }).batchId;
    if (report.reportType === ReportTypes.UTB_REPORT) {
      downloadUtbReportMutation.mutate({ batchId, reportId: report._id });
    } else if (report.file) {
      downloadReportFileMutation.mutate({ report });
    } else {
      downloadBatchReportMutation.mutate({ batchId, reportId: report._id });
    }
  };
  const hasPendingReports = reports?.some(({ status }) => status === ReportStatus.IN_PROGRESS);
  const hasFailedReports = reports?.some(({ status }) => status === ReportStatus.FAILED);

  const indicatorColour = hasFailedReports ? 'red' : hasPendingReports ? 'yellow' : 'green';

  return (
    <Menu position="bottom-end" disabled={reports.length === 0} shadow={theme.shadows.lg}>
      <Menu.Target>
        <Tooltip label="Reports" position="left">
          <Indicator processing color={indicatorColour} disabled={!reports.length}>
            <UnstyledButton>
              <IconBox icon={reportIcon} alt={'report menu'} />
            </UnstyledButton>
          </Indicator>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Label>Reports</Menu.Label>
        {reports?.map((report, index) => (
          <ReportMenuItem
            key={report._id}
            report={report}
            isLast={index === reports.length - 1}
            onDownload={() => handleDownload(report)}
            onDelete={() => deleteReportMutation.mutate({ params: { reportId: report._id } })}
          />
        ))}
      </Menu.Dropdown>
    </Menu>
  );
};
